import { gql, useMutation } from "@apollo/client";
import { Field, StackApiContext } from "@comet/admin";
import { Button, Grid, Typography } from "@mui/material";
import { ContentWrapper } from "@src/components";
import { AsyncSelect } from "@src/components/fields/AsyncSelect";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import Form from "@src/components/Form";
import PackageSelect, { type PackageOption } from "@src/components/select/PackageSelect";
import {
    type GQLCreateLicencesInput,
    type GQLImportLicencesMutation,
    type GQLImportLicencesMutationVariables,
    GQLLicenceType,
} from "@src/graphql.generated";
import { type FC, type PropsWithChildren, type ReactElement, useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import CsvImport, { convertCsvToCodesArray } from "./CsvImport";

interface FormValues extends GQLCreateLicencesInput {
    packageInput: PackageOption;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    csv: any;
}

const importLicencesMutation = gql`
    mutation ImportLicences($input: ImportLicencesInput!) {
        importLicences(input: $input) {
            created
            alreadyExists
            errors
        }
    }
`;

const ImportInformation: FC<PropsWithChildren<{ result: string[]; title: ReactElement }>> = ({ result, title }) => {
    if (result.length === 0) {
        return null;
    }

    return (
        <Grid size={12}>
            <Typography variant="h4">{title}</Typography>
            {result.map((code) => (
                <Typography key={code}>{code}</Typography>
            ))}
        </Grid>
    );
};

const CsvImportForm: FC<PropsWithChildren> = () => {
    const intl = useIntl();
    const stackApi = useContext(StackApiContext);

    const [importMutation] = useMutation<GQLImportLicencesMutation, GQLImportLicencesMutationVariables>(importLicencesMutation);
    const [importResult, setImportResult] = useState<{ created: Array<string>; alreadyExists: Array<string>; errors: Array<string> }>();

    const submit = async ({ packageInput, csv, type }: FormValues) => {
        const codes = await convertCsvToCodesArray(csv.file);
        const response = await importMutation({
            variables: {
                input: {
                    packageId: packageInput.id,
                    codes,
                    type,
                },
            },
        });

        if (response.data) {
            setImportResult(response.data.importLicences);
        }
    };

    const licenceTypeOptions = [
        { value: GQLLicenceType.Printed, label: intl.formatMessage({ id: "reviermanager.codes.type.printed", defaultMessage: "Printed" }) },
        { value: GQLLicenceType.Online, label: intl.formatMessage({ id: "reviermanager.codes.type.online", defaultMessage: "Online" }) },
    ];

    return (
        <>
            {importResult ? (
                <ContentWrapper>
                    <Grid container spacing={4}>
                        <ImportInformation
                            result={importResult.created}
                            title={<FormattedMessage id="reviermanager.codes.form.import.result.created" defaultMessage="Erstellte Codes" />}
                        />
                        <ImportInformation
                            result={importResult.alreadyExists}
                            title={
                                <FormattedMessage
                                    id="reviermanager.codes.form.import.result.alreadyExists"
                                    defaultMessage="Bereits existierende Codes"
                                />
                            }
                        />
                        <ImportInformation
                            result={importResult.errors}
                            title={
                                <FormattedMessage
                                    id="reviermanager.codes.form.import.result.error"
                                    defaultMessage="Fehler beim erstellen des Codes"
                                />
                            }
                        />
                        <Grid size={12}>
                            <Button variant="contained" color="primary" onClick={() => stackApi?.goBack()}>
                                <Typography>
                                    <FormattedMessage id="reviermanager.button.goBack" defaultMessage="Zurück" />
                                </Typography>
                            </Button>
                        </Grid>
                    </Grid>
                </ContentWrapper>
            ) : (
                <Form
                    mode="add"
                    onSubmit={submit}
                    title={intl.formatMessage({ id: "reviermanager.codes.form.import.title", defaultMessage: "Neue Codes importieren" })}
                    onAfterSubmit={() => {
                        // do nothing
                    }}
                >
                    <StaticSelectField
                        name="type"
                        label={intl.formatMessage({ id: "reviermanager.code.type", defaultMessage: "Typ" })}
                        options={licenceTypeOptions}
                        required
                    />
                    <AsyncSelect
                        name="packageInput"
                        label={intl.formatMessage({ id: "reviermanager.code.package", defaultMessage: "Paket" })}
                        component={PackageSelect}
                        required
                    />
                    <Field name="csv" component={CsvImport} required />
                </Form>
            )}
        </>
    );
};

export default CsvImportForm;
